import { getAttributionPayload } from "@/lib/attribution";
import EmployerAccessForm from "@/components/EmployerAccessForm";
import { canCreateAccount } from "@/lib/employerAccessRequest";
import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "@/lib/router-compat";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, User, ArrowRight, Home, Hammer, TrendingUp, DollarSign, Building, MapPin, BookOpen, CheckCircle2, ChevronRight, ShieldCheck, Copy, Wand2, Phone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/logo-new-final.png";
import lifemartCobrand from "@/assets/lp/lifemart-cobrand.svg";
import { useLifeMart } from "@/hooks/useLifeMart";
import { trackEvent } from "@/lib/analytics";
import { upsertUserAttributes } from "@/lib/userAttributes";
import { readAffiliateParams, clearAffiliateParams } from "@/lib/affiliateTracking";
import { logLpEvent, readCampaignParams } from "@/lib/lpEvents";
import { getLastZip, isValidZip } from "@/lib/lastZip";
import { getHeroCategory, markHeroStackFunnel, setHeroStackPhoneProvided } from "@/lib/heroCategory";
import { readQuizBuffer } from "@/lib/homieQuiz";




function generateStrongPassword(): string {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const digits = "23456789";
  const special = "!@#$%&*";
  const all = upper + lower + digits + special;
  const pick = (s: string) => s[Math.floor(Math.random() * s.length)];
  let pw = [pick(upper), pick(lower), pick(digits), pick(special)];
  for (let i = pw.length; i < 14; i++) pw.push(pick(all));
  return pw.sort(() => Math.random() - 0.5).join("");
}

function getPasswordStrength(pw: string): { label: string; color: string; pct: number; checks: { len: boolean; upper: boolean; number: boolean } } {
  const checks = { len: pw.length >= 8, upper: /[A-Z]/.test(pw), number: /\d/.test(pw) };
  const passed = [checks.len, checks.upper, checks.number].filter(Boolean).length;
  if (passed <= 1) return { label: "Weak", color: "bg-destructive", pct: 33, checks };
  if (passed === 2) return { label: "Fair", color: "bg-amber-500", pct: 66, checks };
  return { label: "Strong", color: "bg-green-500", pct: 100, checks };
}

const tracks = [
  { key: "renter", label: "I'm currently renting", icon: Building, description: "Build your financial foundation and prepare for homeownership" },
  { key: "homebuyer", label: "Buying a home", icon: Home, description: "Get guided through every step of the buying journey" },
  { key: "homeowner", label: "I already own", icon: Hammer, description: "Discover improvements, maintenance tips & earn rewards" },
  { key: "seller", label: "Preparing to sell", icon: TrendingUp, description: "Maximize your home's value and navigate selling" },
  { key: "investor", label: "Investing in property", icon: DollarSign, description: "Analyze deals, secure financing & build your portfolio" },
  { key: "relocator", label: "Relocating", icon: MapPin, description: "Compare costs, explore neighborhoods & find your new home" },
];

type AuthStep = "credentials" | "track-select" | "confirmed";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const [isSignUp, setIsSignUp] = useState(searchParams.get("signin") !== "true");
  const signupOnly = searchParams.get("signup_only") === "true";
  const isRubenFlow = signupOnly && searchParams.get("returnTo") === "/experts/ruben-nazario";
  // High-intent paid funnels that promise "see your Stack" — these must never
  // be interrupted by the generic track picker after signup.
  const stackFunnelReturnTo = searchParams.get("returnTo") ?? "";
  const stackFunnelSource =
    signupOnly &&
    (stackFunnelReturnTo === "/grant-finder" || stackFunnelReturnTo.startsWith("/grant-finder?"))
      ? (["ruben", "hero-stack"] as const).find((s) => s === searchParams.get("source")) ?? null
      : null;

  const isRubenStackFlow = stackFunnelSource !== null;
  const isHeroStackFlow = stackFunnelSource === "hero-stack";
  // Homie quiz promised "save my score" — go straight back to the results.
  const isHomieQuizFlow =
    signupOnly &&
    searchParams.get("source") === "homie-quiz" &&
    stackFunnelReturnTo.startsWith("/homie");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<AuthStep>("credentials");
  const trackKeys = tracks.map((t) => t.key);
  const initialTrack = (() => {
    const fromUrl = searchParams.get("track");
    if (fromUrl && trackKeys.includes(fromUrl)) return fromUrl;
    return null;
  })();
  const [selectedTrack, setSelectedTrack] = useState<string | null>(initialTrack);
  const [savingTrack, setSavingTrack] = useState(false);
  const { signIn, signUp, user, loading: authLoading } = useAuth();
  const { showLifeMartBranding, isLifeMartMember } = useLifeMart();
  // Employer-only access: new accounts only via an employer page or LifeMart.
  const signupAllowed = canCreateAccount({ employer: searchParams.get("employer"), lifeMartVerified: isLifeMartMember });
  const blockedSignup = isSignUp && !signupAllowed;
  const navigate = useNavigate();
  const { toast } = useToast();
  const safePath = (raw: string | null): string =>
    raw?.startsWith("/") && !raw.startsWith("//") ? raw : "/dashboard";

  // Capture returnTo once at mount. Later param changes (or a navigation that
  // drops the query string) must never turn the destination into /dashboard.
  const returnToRef = useRef<string | null>(searchParams.get("returnTo"));
  // True from the moment signup/sign-in starts until we navigate ourselves —
  // keeps the "already authenticated" effect from hijacking the flow when the
  // Supabase session lands mid-way through track selection.
  const flowInProgressRef = useRef(false);

  const goAfterAuth = (opts?: { replace?: boolean }) => {
    flowInProgressRef.current = false;
    navigate(safePath(returnToRef.current), opts);
  };

  const heroStackZip = useMemo(() => {
    if (!isHeroStackFlow) return null;
    const m = /[?&]zip=(\d{5})(?:&|$)/.exec(returnToRef.current ?? "");
    if (m) return m[1];
    const stored = getLastZip();
    return isValidZip(stored) ? stored : null;
  }, [isHeroStackFlow]);

  useEffect(() => {
    if (!isHeroStackFlow) return;
    markHeroStackFunnel();
    trackEvent("hero_stack_signup_screen_viewed", { has_zip: Boolean(heroStackZip) });
    logLpEvent("hero_stack_signup_screen_viewed", { has_zip: Boolean(heroStackZip) });
  }, [heroStackZip, isHeroStackFlow]);

  // If user is already authenticated on arrival, skip the form and honor returnTo
  useEffect(() => {
    if (!authLoading && user && !flowInProgressRef.current) {
      navigate(safePath(returnToRef.current), { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, navigate]);

  // Persist preselected track so Onboarding can read it after auth confirmation
  useEffect(() => {
    if (initialTrack) {
      try {
        localStorage.setItem("ha_preselected_track", initialTrack);
      } catch (_) {}
    }
  }, [initialTrack]);

  const normalizePhone = (raw: string) => raw.replace(/\D/g, "");
  const validatePhone = (raw: string) => {
    if (!raw) return { valid: true, normalized: "" };
    const digits = normalizePhone(raw);
    if (digits.length === 10) return { valid: true, normalized: digits };
    if (digits.length === 11 && digits.startsWith("1")) return { valid: true, normalized: digits.slice(1) };
    return { valid: false, normalized: digits };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanFirst = firstName.trim().slice(0, 50);
    const cleanLast = lastName.trim().slice(0, 50);
    if (blockedSignup) return;
    if (isSignUp) {
      if (!cleanFirst || !cleanLast) {
        setNameError("Please enter your first and last name.");
        return;
      }
      if (phone.trim()) {
        const { valid } = validatePhone(phone);
        if (!valid) {
          setPhoneError("Please enter a valid 10-digit U.S. mobile number, or leave this blank.");
          return;
        }
      }
    }
    setLoading(true);
    flowInProgressRef.current = true;

    if (isSignUp) {
      const fullName = `${cleanFirst} ${cleanLast}`;
      const { error } = await signUp(email, password, fullName, {
        first_name: cleanFirst,
        last_name: cleanLast,
        full_name: fullName,
      });
      if (error) {
        flowInProgressRef.current = false;
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        const refCode = searchParams.get("ref");
        if (refCode) localStorage.setItem("ha_referral_code", refCode);
        const phoneCheck = phone.trim() ? validatePhone(phone) : { valid: false, normalized: "" };
        const phoneProvided = phoneCheck.valid && Boolean(phoneCheck.normalized);
        trackEvent("signup_completed", { source: "auth_page", phone_provided: phoneProvided });
        if (isHeroStackFlow) {
          trackEvent("hero_stack_signup_completed", { has_zip: Boolean(heroStackZip), phone_provided: phoneProvided });
          logLpEvent("hero_stack_signup_completed", { has_zip: Boolean(heroStackZip), phone_provided: phoneProvided });
          markHeroStackFunnel();
        }
        const returnTo = returnToRef.current;
        // Persist any buffered survey data before moving to track selection
        const { data: { user: newUser } } = await supabase.auth.getUser();
        if (newUser) {
          // Persist lead source attribution from URL params
          // Fall back to first-touch attribution so every signup records a source.
          const leadSource =
            searchParams.get("ref") || searchParams.get("source") || getAttributionPayload().signup_source || null;
          const campaign = readCampaignParams();
          const campaignAttrs = Object.fromEntries(
            Object.entries(campaign).filter(([, v]) => Boolean(v)),
          );
          const heroCategory = getHeroCategory();
          if (isHeroStackFlow) setHeroStackPhoneProvided(phoneProvided);
          upsertUserAttributes({
            first_name: cleanFirst,
            last_name: cleanLast,
            ...(phoneProvided ? { phone: phoneCheck.normalized } : {}),
            ...(leadSource ? { lead_source: leadSource } : {}),
            ...(heroCategory ? { hero_category: heroCategory } : {}),
            ...(searchParams.get("employer") ? { employer_name: searchParams.get("employer")!.slice(0, 120) } : {}),
            ...campaignAttrs,
          }).catch(() => {});
          if (phoneProvided) {
            supabase.rpc("record_sms_consent", { p_version: "2026-09-signup-v1" }).then(() => {}, () => {});
          }


          // Persist inbound affiliate attribution and link click record to this user
          const affParams = readAffiliateParams();
          if (affParams) {
            upsertUserAttributes({
              aff_sub_id1:  affParams.sub_id1  ?? null,
              aff_sub_id2:  affParams.sub_id2  ?? null,
              aff_sub_id3:  affParams.sub_id3  ?? null,
              aff_sub_id4:  affParams.sub_id4  ?? null,
              aff_source:   affParams.source   ?? null,
              aff_medium:   affParams.medium   ?? null,
              aff_ref:      affParams.ref      ?? null,
              aff_click_id: affParams.click_id,
            }).catch(() => {});
            supabase.from("affiliate_clicks" as any)
              .update({ user_id: newUser.id })
              .eq("click_id", affParams.click_id)
              .then(({ error }) => { if (!error) clearAffiliateParams(); });
          }
          // Carry the ZIP the visitor already gave us (hero form or returnTo
          // param) onto their profile so no tool asks for it twice.
          const funnelZip = (() => {
            const m = /[?&]zip=(\d{5})(?:&|$)/.exec(returnTo ?? "");
            if (m) return m[1];
            const stored = getLastZip();
            return isValidZip(stored) ? stored : null;
          })();
          if (funnelZip) {
            upsertUserAttributes({ zip_code: funnelZip }).catch(() => {});
          }

          // Homie Readiness Quiz taken before signup — attach it to the account
          // so the results screen can come back saved.
          const quizBuffer = readQuizBuffer();
          if (quizBuffer) {
            try {
              await supabase.from("readiness_quiz_results").insert({
                user_id: newUser.id,
                score: quizBuffer.score,
                tier: quizBuffer.tier,
                band: quizBuffer.band,
                answers: quizBuffer.answers as any,
                zip_code: quizBuffer.answers.zip ?? null,
                hero_category: quizBuffer.heroCategory,
                employer_name: quizBuffer.answers.employerName ?? null,
              });
              upsertUserAttributes({
                credit_readiness_score: quizBuffer.score,
                credit_readiness_tier: quizBuffer.tier,

                ...(quizBuffer.answers.employerName
                  ? { employer_name: quizBuffer.answers.employerName }
                  : {}),
              }).catch(() => {});
              // The buffer stays until /homie rehydrates the results screen.
            } catch (_) {}
          }

          const buffered = localStorage.getItem("ha_survey_routing");
          if (buffered) {
            try {
              const parsed = JSON.parse(buffered);
              await supabase.from("routed_leads").insert({
                user_id: newUser.id,
                assigned_to: parsed.assignedTo || "randy",
                segment: parsed.segment || "nurture",
                source: parsed.source || "funnel",
                readiness_score: parsed.readinessScore || 0,
                answers: parsed,
                email: newUser.email || null,
              });
              localStorage.removeItem("ha_survey_routing");
            } catch (_) {}
          }

        }
        // High-intent funnels (e.g. credit-match) skip manual track selection —
        // the funnel itself will set the track downstream.
        if (returnTo === "/onboarding/credit-match") {
          toast({ title: "Account created!", description: "Let's reveal your matched offer." });
          goAfterAuth();
        } else if (isRubenStackFlow || isHomieQuizFlow) {
          // Paid funnel promised "see your Stack" / "save my score" — never
          // interrupt with the generic track picker.
          toast({
            title: "Account created!",
            description: isHomieQuizFlow ? "Your score is saved." : "Let's build your Stack.",
          });
          await handleTrackConfirm(initialTrack || "homebuyer", { silent: true });
          goAfterAuth();

        } else {
          toast({ title: "Account created!", description: "Let's personalize your experience." });
          setStep("track-select");
        }
      }
    } else {
      const { error } = await signIn(email, password);
      if (error) {
        flowInProgressRef.current = false;
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          // Check if user has ever selected a track
          const { data: surveyData } = await supabase
            .from("survey_responses")
            .select("id")
            .eq("user_id", authUser.id)
            .eq("completed", true)
            .limit(1);

          if (!surveyData || surveyData.length === 0) {
            // No track selected yet — force track selection even if returnTo
            // exists, EXCEPT for high-intent funnels which set it implicitly.
            trackEvent("login", { hasOnboarding: false });
            if (isRubenStackFlow) {
              await handleTrackConfirm(initialTrack || "homebuyer", { silent: true });
              goAfterAuth();
            } else {
              setStep("track-select");
            }
          } else {
            trackEvent("login", { hasOnboarding: true });
            goAfterAuth();
          }
        } else {
          goAfterAuth();
        }
      }
    }
    setLoading(false);
  };

  const handleTrackConfirm = async (trackOverride?: string, opts?: { silent?: boolean }) => {
    const track = trackOverride || selectedTrack;
    if (!track) return;
    if (!opts?.silent) setSavingTrack(true);

    const currentUser = user || (await supabase.auth.getUser()).data.user;
    const answers = { 1: [tracks.find(t => t.key === track)?.label || track] };

    await supabase.from("survey_responses").insert({
      user_id: currentUser?.id || null,
      answers: answers as any,
      completed: true,
    });

    // Persist the chosen track to BOTH user_attributes (analytics surface) and
    // profiles.journey_track (source of truth for the dashboard). Without these
    // writes, the track never lands on the profile and the user appears as
    // "no track selected" in admin/reporting.
    upsertUserAttributes({ journey_track: track }).catch(() => {});
    if (currentUser?.id) {
      supabase
        .from("profiles")
        .update({ journey_track: track })
        .eq("user_id", currentUser.id)
        .then(({ error }) => {
          if (error) console.warn("[auth] profiles.journey_track update failed", error);
        });
    }

    // Kick off the 6-touch member welcome email sequence (touch 1 sends immediately).
    // Fire-and-forget — never block the UI on email enqueue.
    if (currentUser?.id && currentUser?.email) {
      supabase.functions
        .invoke("member-welcome-sequence", {
          body: {
            action: "enqueue",
            user_id: currentUser.id,
            email: currentUser.email,
            first_name:
              firstName.trim() ||
              (currentUser.user_metadata?.first_name as string | undefined) ||
              (currentUser.email.split("@")[0] ?? null),
            track,
          },
        })
        .catch((err) => console.error("member-welcome-sequence enqueue failed:", err));
    }

    setSavingTrack(false);
    trackEvent("track_selected", { track, implicit: !!opts?.silent });
    if (!opts?.silent) setStep("confirmed");
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="flex items-center justify-center gap-4 sm:gap-5 mb-3">
          <Link to="/" className="shrink-0">
            <img src={logo} alt="Home Approach"
              className={showLifeMartBranding
                ? "h-[56px] sm:h-[64px] w-auto"
                : "h-20 sm:h-24 w-auto"} />
          </Link>
          {showLifeMartBranding && (
            <>
              <div aria-hidden className="h-10 w-px bg-border shrink-0" />
              <img src={lifemartCobrand} alt="LifeMart" className="h-8 sm:h-10 w-auto shrink-0" />
            </>
          )}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Credentials */}
          {step === "credentials" && (
            <motion.div
              key="credentials"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {/* Sign Up / Sign In segmented toggle — hidden when signup_only param is set */}
              {!signupOnly && (<>
              <div
                role="tablist"
                aria-label="Authentication mode"
                className="grid grid-cols-2 gap-1 p-1 mb-3 rounded-full border border-border bg-muted/40"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={isSignUp}
                  onClick={() => setIsSignUp(true)}
                  className={`h-9 rounded-full text-sm font-semibold transition-colors ${
                    isSignUp
                      ? "bg-primary text-primary-foreground shadow-xs"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Sign Up
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isSignUp}
                  onClick={() => setIsSignUp(false)}
                  className={`h-9 rounded-full text-sm font-semibold transition-colors ${
                    !isSignUp
                      ? "bg-primary text-primary-foreground shadow-xs"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Sign In
                </button>
              </div>
              </>)}

              <div className="text-center mb-3">
                <h1
                  className="text-2xl text-foreground mb-1"
                  style={{ fontFamily: "var(--heading-font)" }}
                >
                  {blockedSignup
                    ? "Request access through your employer"
                    : isHeroStackFlow
                    ? "Create your account to run your Hero Stack"
                    : isRubenStackFlow
                    ? "Create your account to run your Stack with Ruben"
                    : isRubenFlow
                    ? "Create your account to connect with Ruben"
                    : isSignUp
                    ? "Create Your Account"
                    : "Welcome Back"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {blockedSignup
                    ? "Already a member? Tap Sign In above."
                    : isHeroStackFlow
                    ? heroStackZip
                      ? "Free, no credit card. Your Stack runs the moment your account is created."
                      : "Free, no credit card. Enter your ZIP next to pull your Stack."
                    : isRubenStackFlow
                    ? "Free, no credit card. Enter your ZIP next to pull your grant stack."
                    : isRubenFlow

                    ? "Free, no credit card. Ruben's profile opens right after."
                    : isSignUp
                    ? "Quick setup — takes under 30 seconds"
                    : "Sign in to continue your journey"}
                </p>
              </div>

              {blockedSignup ? (
                <div className="space-y-3">
                  <p className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm text-foreground">
                    <strong>Home Approach is offered through employers.</strong> Tell us where you work and we'll reach out to bring it to you.
                    If your employer already offers it, use the signup link from your HR or benefits team.
                  </p>
                  <EmployerAccessForm source={searchParams.get("source") || "auth-page"} />
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-xs">
                {isSignUp && isHeroStackFlow && heroStackZip && (
                  <div className="rounded-lg border border-primary/15 bg-primary/5 px-3 py-2 text-center text-xs font-semibold text-primary">
                    ZIP saved: {heroStackZip}
                  </div>
                )}
                {isSignUp && (
                  <div className="space-y-1">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                        <Input
                          placeholder="First name"
                          aria-label="First name"
                          autoComplete="given-name"
                          value={firstName}
                          onChange={(e) => { setFirstName(e.target.value); if (nameError) setNameError(null); }}
                          required
                          maxLength={50}
                          className="pl-10"
                        />
                      </div>
                      <Input
                        placeholder="Last name"
                        aria-label="Last name"
                        autoComplete="family-name"
                        value={lastName}
                        onChange={(e) => { setLastName(e.target.value); if (nameError) setNameError(null); }}
                        required
                        maxLength={50}
                      />
                    </div>
                    {nameError && <p className="text-xs text-destructive px-1">{nameError}</p>}
                  </div>
                )}

                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pl-10"
                  />
                </div>
                {isSignUp && password.length > 0 && (() => {
                  const s = getPasswordStrength(password);
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-300 ${s.color}`} style={{ width: `${s.pct}%` }} />
                        </div>
                        <span className={`text-xs font-semibold ${s.pct === 100 ? "text-green-600" : s.pct === 66 ? "text-amber-600" : "text-destructive"}`}>{s.label}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                        <span className={s.checks.len ? "text-green-600" : "text-muted-foreground"}>
                          {s.checks.len ? "✓" : "○"} 8+ characters
                        </span>
                        <span className={s.checks.upper ? "text-green-600" : "text-muted-foreground"}>
                          {s.checks.upper ? "✓" : "○"} Uppercase letter
                        </span>
                        <span className={s.checks.number ? "text-green-600" : "text-muted-foreground"}>
                          {s.checks.number ? "✓" : "○"} Number
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {isSignUp && (
                  <div className="space-y-1">
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <Input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel-national"
                        placeholder="Mobile number"
                        aria-label="Mobile number"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          if (phoneError) setPhoneError(null);
                        }}
                        maxLength={20}
                        className="pl-10"
                        aria-invalid={!!phoneError}
                      />
                    </div>
                    {phoneError && (
                      <p className="text-xs text-destructive px-1">{phoneError}</p>
                    )}
                  </div>
                )}
                {isSignUp && (
                  <button
                    type="button"
                    onClick={() => {
                      const pw = generateStrongPassword();
                      setPassword(pw);
                      navigator.clipboard.writeText(pw).then(() => {
                        toast({ title: "Password generated & copied!", description: "A strong password has been set and copied to your clipboard." });
                      }).catch(() => {
                        toast({ title: "Password generated!", description: "A strong password has been set. Remember to save it." });
                      });
                    }}
                    className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                  >
                    <Wand2 className="h-3 w-3" /> Generate strong password
                  </button>
                )}
                {!isSignUp && (
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!email.trim()) {
                          toast({ title: "Enter your email first", description: "We'll send a reset link to that address.", variant: "destructive" });
                          return;
                        }
                        const { error } = await supabase.auth.resetPasswordForEmail(email, {
                          redirectTo: `${window.location.origin}/reset-password`,
                        });
                        if (error) {
                          toast({ title: "Error", description: error.message, variant: "destructive" });
                        } else {
                          toast({ title: "Check your email", description: "We sent a password reset link." });
                        }
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
                {isSignUp && (
                  <p className="text-[10px] leading-relaxed text-muted-foreground">
                    By clicking "Create Account," I agree to receive helpful information and account alerts from homeapproach.com, and, if I entered a mobile number, calls and texts from Home Approach at that number, including automated messages. Consent is not required to use Home Approach. Message &amp; data rates may apply; I can opt out anytime.
                  </p>
                )}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Loading..." : isSignUp ? "Create Account" : "Sign In"}
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </form>
              )}

              {!signupOnly && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsSignUp(!isSignUp)}
                className="w-full mt-4"
              >
                {isSignUp ? "Sign in to existing account" : "Create a new account"}
                <ArrowRight size={16} className="ml-2" />
              </Button>
              )}
            </motion.div>
          )}

          {/* Step 2: Track Selection */}
          {step === "track-select" && (
            <motion.div
              key="track-select"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.25 }}
              className="rounded-2xl border border-border bg-card p-6 shadow-xs"
            >
              <div className="text-center mb-5">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-3">
                  <CheckCircle2 size={14} />
                  Account created
                </div>
                <h2
                  className="text-xl text-foreground font-bold mb-1"
                  style={{ fontFamily: "var(--heading-font)" }}
                >
                  Where are you on your journey?
                </h2>
                <p className="text-sm text-muted-foreground">
                  This helps us personalize your tools, content & rewards.
                </p>
              </div>

              <div className="grid gap-2.5 mb-5">
                {tracks.map((track) => {
                  const isSelected = selectedTrack === track.key;
                  return (
                    <button
                      key={track.key}
                      onClick={() => setSelectedTrack(track.key)}
                      className={`group flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition-all ${
                        isSelected
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border bg-background hover:border-primary/40 hover:bg-primary/[0.02]"
                      }`}
                    >
                      <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
                        isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground group-hover:text-primary"
                      }`}>
                        <track.icon size={16} />
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-semibold ${isSelected ? "text-foreground" : "text-foreground"}`}>
                          {track.label}
                        </p>
                        <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                          {track.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <Button
                onClick={() => handleTrackConfirm()}
                disabled={!selectedTrack || savingTrack}
                className="w-full gap-1"
              >
                {savingTrack ? "Saving..." : "Continue"}
                <ChevronRight size={16} />
              </Button>

              <button
                onClick={() => handleTrackConfirm("explorer")}
                className="block w-full text-center text-xs text-muted-foreground mt-3 hover:text-foreground transition-colors"
              >
                I'm just exploring — skip for now
              </button>
            </motion.div>
          )}

          {/* Step 3: Confirmed */}
          {step === "confirmed" && (
            <motion.div
              key="confirmed"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className="rounded-2xl border border-border bg-card p-8 text-center shadow-xs"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.1 }}
              >
                <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-primary" />
              </motion.div>

              <h2
                className="text-2xl text-foreground mb-2 font-bold"
                style={{ fontFamily: "var(--heading-font)" }}
              >
                You're All Set!
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {"Your personalized dashboard is ready."}
              </p>

              <Button
                size="lg"
                className="w-full gap-2"
                onClick={() => {
                  goAfterAuth();
                }}
              >
                {returnToRef.current ? "See My Results" : "Go to My Dashboard"}
                <ArrowRight size={16} />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Auth;
